import React, { useRef } from 'react';
import api from '../config/api';

function ItemTypeList({ itemTypes, onSelect, onDelete, onCreateNew, onEdit, onRefresh }) {
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    try {
      const response = await api.get('/item-types/export', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `item_types_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting item types:', error);
      alert('Failed to export item types');
    }
  };

  const handleImport = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      // Accept both old and new format for backward compatibility
      if (!['item_types', 'inventory_types'].includes(importData.type)) {
        alert('Invalid file type. Please select an item types export file.');
        return;
      }

      const response = await api.post('/item-types/import', importData);
      
      if (response.data.success) {
        alert(`Import completed!\nImported: ${response.data.imported}\nSkipped: ${response.data.skipped}\n\nErrors:\n${response.data.errors.join('\n')}`);
        if (onRefresh) onRefresh();
      } else {
        alert('Import failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error importing item types:', error);
      alert('Failed to import item types: ' + (error.response?.data?.error || error.message));
    }

    // Reset file input
    event.target.value = '';
  };

  const handleDuplicate = async (itemType) => {
    if (!window.confirm(`Create a duplicate of "${itemType.name}"?`)) {
      return;
    }

    try {
      const response = await api.post(`/item-types/${itemType.id}/duplicate`);
      alert(`Item type duplicated successfully as "${response.data.name}"!`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error duplicating item type:', error);
      alert('Failed to duplicate item type: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Item Types</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn" onClick={handleExport} style={{ backgroundColor: '#27ae60' }}>
            📤 Export
          </button>
          <button className="btn" onClick={handleImport} style={{ backgroundColor: '#3498db' }}>
            📥 Import
          </button>
          <button className="btn" onClick={onCreateNew}>
            Create New Item Type
          </button>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".json"
        style={{ display: 'none' }}
      />

      {itemTypes.length === 0 ? (
        <div className="card">
          <p>No item types yet. Create your first item type to define the structure of your inventory items!</p>
        </div>
      ) : (
        <div className="inventory-types-grid">
          {itemTypes.map(itemType => (
            <div key={itemType.id} className="card">
              <h3>{itemType.name}</h3>
              <p>{itemType.description || 'No description'}</p>
              <p><strong>Fields:</strong> {itemType.fields ? itemType.fields.length : 0}</p>
              
              {itemType.fields && itemType.fields.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Field Types:</strong>
                  <ul style={{ margin: '0.25rem 0', paddingLeft: '1rem', fontSize: '0.9rem' }}>
                    {itemType.fields.slice(0, 3).map((field, index) => (
                      <li key={index} style={{ color: '#666' }}>
                        {field.name} ({field.type})
                        {field.in_view === false && <span style={{ color: '#e67e22', fontSize: '0.8rem' }}> - Detail Only</span>}
                      </li>
                    ))}
                    {itemType.fields.length > 3 && (
                      <li style={{ color: '#999', fontStyle: 'italic' }}>
                        ...and {itemType.fields.length - 3} more fields
                      </li>
                    )}
                  </ul>
                </div>
              )}
              
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-small" onClick={() => onEdit(itemType)}>
                  Edit
                </button>
                <button 
                  className="btn btn-small" 
                  onClick={() => handleDuplicate(itemType)}
                  style={{ backgroundColor: '#9b59b6' }}
                  title="Create a duplicate of this item type"
                >
                  📋 Duplicate
                </button>
                <button 
                  className="btn btn-danger btn-small" 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this item type? This action cannot be undone.')) {
                      onDelete(itemType.id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ItemTypeList;